/**
 * RankedList.tsx — small "Top N" table card.
 *
 * Used on the Overview page for the three rankings (Top by Loan, Deposit,
 * NPA %). Two columns: a label (often a bank name) and a numeric value.
 * Supply `onRowClick` to make each row a navigable link.
 */

import React from 'react';

import EmptyState from './EmptyState';

export interface RankedRow<T> {
	key: string | number;
	label: React.ReactNode;
	value: React.ReactNode;
	rightHint?: React.ReactNode;
	row: T;
}

interface Props<T> {
	rows: RankedRow<T>[];
	onRowClick?: (row: T) => void;
	emptyHint?: string;
}

export default function RankedList<T>({ rows, onRowClick, emptyHint }: Props<T>) {
	const safe = Array.isArray(rows) ? rows : [];
	if (!safe.length) {
		return <EmptyState title="No data" hint={emptyHint} />;
	}
	return (
		<ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column' }}>
			{safe.map((r, i) => (
				<li
					key={r.key}
					className={onRowClick ? 'tbl-clickable-row' : undefined}
					onClick={onRowClick ? () => onRowClick(r.row) : undefined}
					onKeyDown={
						onRowClick
							? (e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										onRowClick(r.row);
									}
								}
							: undefined
					}
					tabIndex={onRowClick ? 0 : undefined}
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 10,
						padding: '8px 4px',
						borderBottom: i === safe.length - 1 ? 'none' : '1px solid var(--ink-100)',
						cursor: onRowClick ? 'pointer' : 'default',
					}}
				>
					<span
						aria-hidden="true"
						style={{
							width: 22,
							flex: '0 0 auto',
							textAlign: 'right',
							color: 'var(--text-faint)',
							fontVariantNumeric: 'tabular-nums',
							fontSize: 11.5,
							fontWeight: 600,
						}}
					>
						{i + 1}.
					</span>
					<span style={{ flex: 1, minWidth: 0 }}>{r.label}</span>
					{r.rightHint && (
						<span style={{ color: 'var(--text-faint)', fontSize: 11.5 }}>{r.rightHint}</span>
					)}
					<span
						style={{
							fontVariantNumeric: 'tabular-nums',
							fontWeight: 600,
							minWidth: 80,
							textAlign: 'right',
						}}
					>
						{r.value}
					</span>
				</li>
			))}
		</ul>
	);
}
